import React, { useEffect, useRef, useState } from "react";
import { FaCaretDown, FaFont, FaLongArrowAltRight, FaPen, FaRegSquare, FaSlash } from "react-icons/fa";

export type U3140PdfAnnotationInsertToolId = "rect" | "line" | "arrow" | "text";

export interface U3140PdfAnnotationShapesMenuButtonProps {
  disabled?: boolean;
  /** Texte du survol du bouton principal (ex. raison du `disabled`). */
  menuButtonTitle?: string;
  /** Outil de placement actif ; `none` = aucun mode placement. */
  activePlacement: "none" | U3140PdfAnnotationInsertToolId;
  onSelectTool: (tool: U3140PdfAnnotationInsertToolId) => void;
  onCancelPlacement: () => void;
  labels: {
    menuTitle: string;
    rect: string;
    line: string;
    arrow: string;
    text: string;
    /** Libellé du lien pour quitter le mode placement (sans changer de forme). */
    clearPlacement: string;
  };
}

const toolBtn =
  "flex flex-col items-center justify-center gap-1 rounded-lg border border-gray-200 bg-white p-3 text-xs font-medium text-gray-800 transition hover:border-blue-400 hover:bg-blue-50/60 focus:outline-hidden focus:ring-2 focus:ring-blue-500/30";

export const U3140PdfAnnotationShapesMenuButton: React.FC<U3140PdfAnnotationShapesMenuButtonProps> = ({
  disabled = false,
  menuButtonTitle,
  activePlacement,
  onSelectTool,
  onCancelPlacement,
  labels,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc, true);
    return () => document.removeEventListener("mousedown", onDoc, true);
  }, [open]);

  const isActive = (id: U3140PdfAnnotationInsertToolId) => activePlacement === id;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        title={menuButtonTitle}
        onClick={() => !disabled && setOpen((o) => !o)}
        className="flex h-[46px] min-w-36 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-xs transition hover:border-gray-400 disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-gray-200 bg-gray-50 text-gray-700">
          {activePlacement === "rect" ? (
            <FaRegSquare className="text-lg" />
          ) : activePlacement === "line" ? (
            <FaSlash className="text-lg" />
          ) : activePlacement === "arrow" ? (
            <FaLongArrowAltRight className="text-lg" />
          ) : activePlacement === "text" ? (
            <FaFont className="text-lg" />
          ) : (
            <FaPen className="text-lg opacity-40" />
          )}
        </span>
        <span className="flex-1 truncate text-left font-medium text-gray-800">{labels.menuTitle}</span>
        <FaCaretDown className={`shrink-0 text-gray-500 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open ? (
        <div
          className="absolute left-0 top-[calc(100%+4px)] z-10000 w-[280px] rounded-lg border border-gray-200 bg-white p-3 shadow-xl"
          role="dialog"
        >
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{labels.menuTitle}</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              className={`${toolBtn} ${isActive("rect") ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" : ""}`}
              onClick={() => {
                onSelectTool("rect");
                setOpen(false);
              }}
            >
              <FaRegSquare className="text-2xl text-gray-800" />
              <span>{labels.rect}</span>
            </button>
            <button
              type="button"
              className={`${toolBtn} ${isActive("line") ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" : ""}`}
              onClick={() => {
                onSelectTool("line");
                setOpen(false);
              }}
            >
              <FaSlash className="text-2xl text-gray-800" />
              <span>{labels.line}</span>
            </button>
            <button
              type="button"
              className={`${toolBtn} ${isActive("arrow") ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" : ""}`}
              onClick={() => {
                onSelectTool("arrow");
                setOpen(false);
              }}
            >
              <FaLongArrowAltRight className="text-2xl text-gray-800" />
              <span>{labels.arrow}</span>
            </button>
            <button
              type="button"
              className={`${toolBtn} ${isActive("text") ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" : ""}`}
              onClick={() => {
                onSelectTool("text");
                setOpen(false);
              }}
            >
              <FaFont className="text-2xl text-gray-800" />
              <span>{labels.text}</span>
            </button>
          </div>
          <button
            type="button"
            className="mt-3 w-full rounded-sm border border-gray-200 px-3 py-2 text-xs text-gray-600 hover:bg-gray-50"
            onClick={() => {
              onCancelPlacement();
              setOpen(false);
            }}
          >
            {labels.clearPlacement}
          </button>
        </div>
      ) : null}
    </div>
  );
};
